import {formatOrderCar} from '../shared/order-car-display';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DataService} from '../services/data.service';
import {UIBlockerService} from '../services/uiblocker.service';
import {RestService} from '../services/rest.service';
import {finalize} from 'rxjs/operators';
import {isNullOrUndefined} from '../shared/is-null-or-undefined';
import {Subscription} from 'rxjs';
import {MapInfoWindow} from '@angular/google-maps';
import {Car, CarsOnMapResponse, CompleteOrder, Order, OrderView} from '../models/api.models';
import {createCarMarkerContent} from '../shared/advanced-marker-content';
import {environment} from '../../environments/environment';

@Component({
    selector: 'app-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MapComponent implements OnInit, OnDestroy {

    @ViewChild('carInfoWindow', {read: MapInfoWindow, static: false}) carInfoWindow: MapInfoWindow;

    carIds: CarFilterOption[] = [];
    filteredCarIds: CarFilterOption[] = [];
    selectedCarId = '';
    carSearchText = '';
    isCarDropdownOpen = false;
    resultOfSelection = '';
    counter = 0;
    timerId?: ReturnType<typeof setInterval>;
    infoWindowTimerId?: ReturnType<typeof setTimeout>;
    carsOnMap?: CarsOnMapResponse;
    carsData: Car[] = [];
    isLoadingMap = false;
    isLoadingCars = false;
    selectedOrder?: CompleteOrder;
    selectedMapMarker?: Marker;
    readonly mapOptions: google.maps.MapOptions = {
        gestureHandling: 'greedy',
        mapId: environment.googleMapsMapId,
        scrollwheel: true
    };
    readonly carInfoWindowOptions: google.maps.InfoWindowOptions = {
        maxWidth: 340,
        pixelOffset: new google.maps.Size(0, -50)
    };
    private readonly defaultLatitude = 44.4268;
    private readonly defaultLongitude = 26.1025;
    mapCenter: google.maps.LatLngLiteral = {lat: this.defaultLatitude, lng: this.defaultLongitude};
    private readonly defaultZoom = 11;
    zoom = this.defaultZoom;
    private trackedCarIdentifier = '';
    private subscriptions = new Subscription();

    markers: Marker[] = [];

    constructor(private restService: RestService,
                private uiBlockerService: UIBlockerService,
                private dataService: DataService,
                private changeDetectorRef: ChangeDetectorRef) {
    }

    ngOnInit() {
        this.carsOnMap = this.dataService.getCurrentCarsOnMap();
        this.subscriptions.add(this.dataService.dataChangedCarsOnMap.subscribe(() => {
            this.carsOnMap = this.dataService.getCurrentCarsOnMap();
            this.parseReceivedCars();
        }));

        this.setCarsData(this.dataService.getCars());
        this.subscriptions.add(this.dataService.dataChangedCars.subscribe(() => {
            this.setCarsData(this.dataService.getCars());
        }));

        this.subscriptions.add(this.dataService.dataChangedSession.subscribe(() => {
            this.getCarsOnMapFromServer();
            this.getCarsData();
        }));

        if (!isNullOrUndefined(this.dataService.getSessionIdentifier())) {
            this.getCarsOnMapFromServer();
            this.getCarsData();
        }

        this.setTimer();
    }

    ngOnDestroy() {
        this.stopTimer();
        this.clearInfoWindowTimer();
        this.subscriptions.unsubscribe();
    }

    setCarsData(cars: Car[]) {
        this.carsData = Array.isArray(cars) ? cars : [];
        const carsByFullIdentifier = new Map<string, CarFilterOption>();
        this.carsData.forEach((car) => {
            const carId = String(car.CarId || '').trim();
            if (carId === '') return;

            const companyIdentifier = String(car.CompanyName || car.CompanyIdentifier || '').trim();
            const carIdentifier = String(car.CarIdentifier || carId).trim();
            const fullIdentifier = this.buildFullCarIdentifier(companyIdentifier, carId);
            carsByFullIdentifier.set(fullIdentifier.toLowerCase(), {
                fullIdentifier,
                carIdentifier,
                carId,
                companyIdentifier
            });
        });
        this.carIds = Array.from(carsByFullIdentifier.values())
            .sort((left, right) => left.fullIdentifier.localeCompare(
                right.fullIdentifier,
                undefined,
                {numeric: true, sensitivity: 'base'}
            ));
        this.filterCarIds(this.carSearchText);
    }

    onCarSearchChanged(value: string) {
        this.carSearchText = value || '';
        this.selectedCarId = '';
        this.trackedCarIdentifier = '';
        this.resultOfSelection = '';
        this.closeMarkerAnnotation();
        this.filterCarIds(this.carSearchText);
        this.isCarDropdownOpen = true;

        if (this.carSearchText === '') {
            this.resetMapPosition();
        }
    }

    toggleCarDropdown() {
        if (this.isLoadingCars) {
            return;
        }
        this.filterCarIds(this.carSearchText);
        this.isCarDropdownOpen = !this.isCarDropdownOpen;
    }

    openCarDropdown() {
        if (!this.isLoadingCars) {
            this.filterCarIds(this.carSearchText);
            this.isCarDropdownOpen = true;
        }
    }

    closeCarDropdown() {
        window.setTimeout(() => {
            this.isCarDropdownOpen = false;
        }, 0);
    }

    selectFirstFilteredCar(event: KeyboardEvent) {
        event.preventDefault();
        if (this.filteredCarIds.length > 0) {
            this.selectCar(this.filteredCarIds[0]);
        }
    }

    selectCar(car: CarFilterOption) {
        this.selectedCarId = car?.fullIdentifier || '';
        this.carSearchText = this.selectedCarId;
        this.trackedCarIdentifier = '';
        this.isCarDropdownOpen = false;
        this.closeMarkerAnnotation();
        if (this.selectedCarId === '') {
            this.resetCarSelection();
            return;
        }

        const carExistsOnMap = this.markers.find((marker) => this.markerMatchesCar(marker, car));
        if (isNullOrUndefined(carExistsOnMap)) {
            this.resultOfSelection = `Car ${this.selectedCarId} is not currently visible on the map.`;
            this.zoom = this.defaultZoom;
        } else {
            this.trackMarker(carExistsOnMap);
            this.openMarkerAnnotation(carExistsOnMap);
        }
    }

    resetCarSelection() {
        this.selectedCarId = '';
        this.carSearchText = '';
        this.trackedCarIdentifier = '';
        this.filterCarIds('');
        this.isCarDropdownOpen = false;
        this.resultOfSelection = '';
        this.closeMarkerAnnotation();
        this.resetMapPosition();
    }

    getCarsOnMapFromServer() {
        if (isNullOrUndefined(this.dataService.getSessionIdentifier()) || this.isLoadingMap) {
            return;
        }

        this.isLoadingMap = true;
        this.restService.getCarsOnMap()
            .pipe(
                finalize(() => {
                    this.isLoadingMap = false;
                    this.uiBlockerService.unBlockUI();
                })
            )
            .subscribe((res) => {
                if (this.dataService.isResponseSuccessful(res)) {
                    this.dataService.setCurrentCarsOnMap(res);
                }
            });
    }

    clickedMarker(marker: Marker) {
        if (this.trackedCarIdentifier === marker.carIdentifier) {
            if (this.selectedMapMarker?.carIdentifier === marker.carIdentifier) {
                this.resetCarSelection();
            } else {
                this.openMarkerAnnotation(marker);
            }
            return;
        }

        this.trackMarker(marker);
        this.openMarkerAnnotation(marker);
    }

    closeCarAnnotation(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.closeMarkerAnnotation();
    }

    onCarAnnotationClosed() {
        this.clearInfoWindowTimer();
        this.selectedMapMarker = undefined;
    }

    openOrderDetails(order: CompleteOrder, event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.selectedOrder = order;
    }

    closeOrderDetails() {
        this.selectedOrder = undefined;
    }

    parseReceivedCars() {
        if (!isNullOrUndefined(this.carsOnMap)) {
            const onlineCars = this.carsOnMap.OnlineCars || [];
            const onlineCarIdentifiers = new Set(onlineCars
                .map(car => String(car.CarIdentifier || '').trim())
                .filter(identifier => identifier !== ''));
            if (onlineCars.length > 0) {
                onlineCars.filter(element => !isNullOrUndefined(element.CarCoords)
                    && Number.isFinite(element.CarCoords.GpsLat)
                    && Number.isFinite(element.CarCoords.GpsLong))
                    .forEach((element) => {
                        const carCoords = element.CarCoords;
                        if (isNullOrUndefined(carCoords)) return;

                        const carId = isNullOrUndefined(element.CarId) ? '' : String(element.CarId).trim();
                        const carData = this.carsData.find((car) => car.CarIdentifier === element.CarIdentifier);
                        const companyIdentifier = String(
                            element.CompanyName
                            || (!isNullOrUndefined(carData) && carData.CompanyName)
                            || element.CompanyIdentifier
                            || (!isNullOrUndefined(carData) && carData.CompanyIdentifier)
                            || ''
                        );
                        const carIdentifier = String(
                            element.CarIdentifier
                            || (!isNullOrUndefined(carData) && carData.CarIdentifier)
                            || carId
                        ).trim();
                        onlineCarIdentifiers.add(carIdentifier);
                        const iconUrlToShow = element.QueuedOrder
                            ? 'assets/images/map-car-queued.svg'
                            : element.IsBusy
                            ? 'assets/images/map-car-busy.svg'
                            : 'assets/images/map-car-available.svg';
                        const markerTitle = this.buildFullCarIdentifier(companyIdentifier, carId);
                        const markerToPush: Marker = {
                            lat: carCoords.GpsLat,
                            lng: carCoords.GpsLong,
                            label: carId,
                            carId,
                            companyIdentifier,
                            carIdentifier,
                            isBusy: Boolean(element.IsBusy),
                            queuedOrder: element.QueuedOrder,
                            position: {lat: carCoords.GpsLat, lng: carCoords.GpsLong},
                            content: createCarMarkerContent(iconUrlToShow, carId, element.QueuedOrder ? '#f9a8d4' : '#ffffff'),
                            title: markerTitle
                        };

                        const markerExits = this.markers.find((x) =>
                            carIdentifier !== '' ? x.carIdentifier === carIdentifier : x.label === markerToPush.label);
                        if (!isNullOrUndefined(markerExits)) {
                            markerExits.lat = markerToPush.lat;
                            markerExits.lng = markerToPush.lng;
                            markerExits.label = markerToPush.label;
                            markerExits.carId = markerToPush.carId;
                            markerExits.companyIdentifier = markerToPush.companyIdentifier;
                            markerExits.carIdentifier = markerToPush.carIdentifier;
                            markerExits.isBusy = markerToPush.isBusy;
                            markerExits.queuedOrder = markerToPush.queuedOrder;
                            markerExits.position = markerToPush.position;
                            markerExits.content = markerToPush.content;
                            markerExits.title = markerToPush.title;
                            if (!markerExits.isBusy) {
                                markerExits.activeOrder = undefined;
                                markerExits.isLoadingOrder = false;
                                markerExits.orderLookupComplete = false;
                            }
                        } else {
                            this.markers.push(markerToPush);
                        }
                    });
            }
            this.markers = this.markers.filter(marker => onlineCarIdentifiers.has(marker.carIdentifier));

            if (this.selectedMapMarker
                && !this.markers.some(marker => marker.carIdentifier === this.selectedMapMarker?.carIdentifier)) {
                this.carInfoWindow.close();
                this.selectedMapMarker = undefined;
            }

            if (this.selectedCarId !== '') {
                const selectedCar = this.carIds.find(car => car.fullIdentifier === this.selectedCarId);
                const selectedMarker = this.markers.find((marker) => this.trackedCarIdentifier !== ''
                    ? marker.carIdentifier === this.trackedCarIdentifier
                    : !isNullOrUndefined(selectedCar) && this.markerMatchesCar(marker, selectedCar));
                if (!isNullOrUndefined(selectedMarker)) {
                    this.resultOfSelection = '';
                    this.setMapCenter(selectedMarker.lat, selectedMarker.lng);
                } else {
                    this.resultOfSelection = `Car ${this.selectedCarId} is not currently visible on the map.`;
                }
            }
        }
    }

    getMarkerTitle(marker: Marker): string {
        const company = String(marker.companyIdentifier || '').trim().toUpperCase();
        return [company, marker.carId].filter((part) => part !== '').join(' ');
    }

    getOrderIdentifier(orderWrapper: OrderView | undefined): string {
        if (isNullOrUndefined(orderWrapper)) {
            return '';
        }
        const order: Order = 'Order' in orderWrapper && orderWrapper.Order ? orderWrapper.Order : orderWrapper;
        return isNullOrUndefined(order.OrderIdentifier) ? '' : String(order.OrderIdentifier);
    }

    setTimer() {
        this.timerId = setInterval(() => {
            this.getCarsOnMapFromServer();
        }, 5000);
    }

    stopTimer() {
        clearInterval(this.timerId);
    }

    getCarsData() {
        if (isNullOrUndefined(this.dataService.getSessionIdentifier()) || this.isLoadingCars) {
            return;
        }

        this.isLoadingCars = true;
        this.restService.getCars()
            .pipe(finalize(() => {
                this.isLoadingCars = false;
                this.uiBlockerService.unBlockUI();
            }))
            .subscribe((response) => {
                if (this.dataService.isResponseSuccessful(response)) {
                    this.dataService.setCars(response.Cars || []);
                }
            });
    }

    private openMarkerAnnotation(marker: Marker) {
        this.selectedMapMarker = marker;

        if (marker.isBusy && !marker.isLoadingOrder && marker.carIdentifier) {
            this.loadActiveOrder(marker);
        }

        this.changeDetectorRef.detectChanges();
        this.clearInfoWindowTimer();
        this.infoWindowTimerId = window.setTimeout(() => {
            if (this.selectedMapMarker?.carIdentifier === marker.carIdentifier) {
                this.carInfoWindow.open();
            }
        }, 0);
    }

    private loadActiveOrder(marker: Marker) {
        marker.isLoadingOrder = true;
        marker.orderLookupComplete = false;
        marker.activeOrder = undefined;

        this.restService.getActiveOrder(marker.carIdentifier)
            .pipe(finalize(() => {
                marker.isLoadingOrder = false;
                marker.orderLookupComplete = true;
                this.uiBlockerService.unBlockUI();
            }))
            .subscribe({
                next: (response) => {
                    marker.activeOrder = this.dataService.isResponseSuccessful(response)
                        ? response.CompleteOrder
                        : undefined;
                },
                error: () => {
                    marker.activeOrder = undefined;
                }
            });
    }

    private resetMapPosition() {
        this.setMapCenter(this.defaultLatitude, this.defaultLongitude);
        this.zoom = this.defaultZoom;
    }

    private trackMarker(marker: Marker) {
        const fullIdentifier = this.buildFullCarIdentifier(
            marker.companyIdentifier,
            marker.carId
        );
        this.selectedCarId = fullIdentifier;
        this.carSearchText = fullIdentifier;
        this.trackedCarIdentifier = marker.carIdentifier;
        this.filterCarIds(fullIdentifier);
        this.isCarDropdownOpen = false;
        this.resultOfSelection = '';
        this.setMapCenter(marker.lat, marker.lng);
        this.zoom = 15;
    }

    private closeMarkerAnnotation() {
        this.clearInfoWindowTimer();
        this.carInfoWindow?.close();
        this.selectedMapMarker = undefined;
    }

    private clearInfoWindowTimer() {
        if (!isNullOrUndefined(this.infoWindowTimerId)) {
            clearTimeout(this.infoWindowTimerId);
            this.infoWindowTimerId = undefined;
        }
    }

    trackByItems(index: number, car: Marker): string {
        Boolean(index);
        return car.carIdentifier || car.label;
    }

    private filterCarIds(search: string) {
        const normalizedSearch = String(search || '').trim().toLowerCase();
        this.filteredCarIds = normalizedSearch === ''
            ? this.carIds.slice()
            : this.carIds.filter((car) =>
                car.fullIdentifier.toLowerCase().indexOf(normalizedSearch) >= 0
                || car.carIdentifier.toLowerCase().indexOf(normalizedSearch) >= 0
                || car.carId.toLowerCase().indexOf(normalizedSearch) >= 0);
    }

    private buildFullCarIdentifier(companyIdentifier: string, carIdentifier: string): string {
        return formatOrderCar({CompanyIdentifier: companyIdentifier, CarId: carIdentifier});
    }

    private markerMatchesCar(marker: Marker, car: CarFilterOption): boolean {
        if (marker.carIdentifier !== car.carIdentifier) return false;

        const markerCompany = String(marker.companyIdentifier || '').trim();
        const carCompany = String(car.companyIdentifier || '').trim();
        return markerCompany === '' || carCompany === ''
            || markerCompany.localeCompare(carCompany, undefined, {sensitivity: 'base'}) === 0;
    }

    private setMapCenter(lat: number, lng: number) {
        this.mapCenter = {lat, lng};
    }

}

interface Marker {
    lat: number;
    lng: number;
    label: string;
    carId: string;
    companyIdentifier: string;
    carIdentifier: string;
    isBusy: boolean;
    position: google.maps.LatLngLiteral;
    content: HTMLElement;
    title: string;
    activeOrder?: CompleteOrder;
    queuedOrder?: CompleteOrder;
    isLoadingOrder?: boolean;
    orderLookupComplete?: boolean;
}

interface CarFilterOption {
    fullIdentifier: string;
    carIdentifier: string;
    carId: string;
    companyIdentifier: string;
}
